import type { AnyRule } from "@protontech/autofill/types";
import type { Fnode } from "@protontech/fathom";
import { domQuery, rule, type } from "@protontech/fathom";
import { flagCluster, isPredictedField, isPredictedForm, removePredictionFlag, setCachedPredictionScore } from "./flags";
import { isShadowElement } from "./shadow-dom";
import { isVisibleField, isVisibleForm } from "./visible";

type Override = {
  form: HTMLElement;
  formType: string;
  fields: {
    field: HTMLElement;
    fieldType: string;
  }[];
};

/** Cache for elements that require manual overrides due to detection model
 * limitations. Custom elements and shadow DOM hosts often can't be properly
 * classified by ML models, so we track them here to apply website-specific
 * handling instead. TODO: Remove once we have proper custom element support. */
export const OVERRIDE_FORMS = new Set<HTMLElement>();
export const OVERRIDE_FIELDS = new Set<HTMLElement>();

export const addFormOverride = (el: HTMLElement) => OVERRIDE_FORMS.add(el);
export const addFieldOverride = (el: HTMLElement) => OVERRIDE_FIELDS.add(el);

export const clearOverrides = () => {
  OVERRIDE_FORMS.clear();
  OVERRIDE_FIELDS.clear();
};

export const getOverridableForms = () => Array.from(OVERRIDE_FORMS);
export const getOverridableFields = () => Array.from(OVERRIDE_FIELDS);

const matchFormOverrides = () => domQuery(getOverridableForms);
const matchFieldOverrides = () => domQuery(getOverridableFields);

/** NOTE: these guards ensure that overrides have a cached prediction
 * and are visible. Certain custom-elements are pre-rendered in the
 * DOM and should only be processed when becoming "interactive". */
const acceptFormOverride = (fnode: Fnode) => isPredictedForm(fnode) && isVisibleForm(fnode.element);
const acceptFieldOverride = (fnode: Fnode) => isPredictedField(fnode) && isVisibleField(fnode.element);

export const overrides: AnyRule[] = [
  rule(matchFormOverrides(), type("override-form"), {}),
  rule(matchFieldOverrides(), type("override-field"), {}),

  rule(type("override-form").when(acceptFormOverride), type("form-candidate"), {}),
  rule(type("override-field").when(acceptFieldOverride), type("field-candidate"), {}),
];

/** Override a detection result:
 * 1. Check if target form is visible before processing
 * 2. Remove any existing prediction flags from form and fields
 * 3. Set cached prediction scores with confidence of 1.0
 * 4. Add shadow elements to override cache or flag non-form containers as clusters */
export const flagOverride = ({ form, formType, fields }: Override) => {
  if (isVisibleForm(form)) {
    removePredictionFlag(form);
    setCachedPredictionScore(form, formType, 1);

    if (isShadowElement(form)) addFormOverride(form);
    else if (form.tagName !== "FORM") flagCluster(form);

    fields.forEach(({ field, fieldType }) => {
      removePredictionFlag(field);
      setCachedPredictionScore(field, fieldType, 1);
      if (isShadowElement(field)) addFieldOverride(field);
    });
  }
};
